import { kokoroClient } from './kokoroClient';
import { synthesizeBlendLocal } from './speakKokoroBlend';
import { voiceBundle } from './voiceBundle';
import { KOKORO_VOICES } from './voiceMapping';
import type { KokoroVoiceId } from './voiceMapping';
import {
  ensureZeroTTSBytes,
  enumerateAtoms,
  ztHashHex,
} from './zeroTTSSelector';
import {
  atomBucketName,
  pruneStalePersonaBuckets,
  readManifest,
  writeManifest,
} from './zeroTTSManifest';
import type {
  AtomEntry,
  AtomManifest,
  TTSProfile,
  VoiceIdentity,
  ZeroTTSDepth,
} from './zeroTTSProfile';
import { fingerprintVoiceIdentity } from './zeroTTSProfile';

export interface InstallOptions {
  /** Stage 26.6: full voice identity (single voiceId OR blend VoiceSpec). */
  identity: VoiceIdentity;
  /** Stage 40: cache depth — included in the fingerprint so switching
   *  classic/medium/long invalidates the prior bucket. */
  depth?: ZeroTTSDepth;
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
}

function baseVoiceIdOf(identity: VoiceIdentity): string {
  if (identity.kind === 'single') return identity.voiceId;
  return KOKORO_VOICES[identity.spec.voiceA] ?? 'unknown';
}

async function renderAtom(
  identity: VoiceIdentity,
  text: string,
): Promise<{ wav: Blob; durationMs: number; sizeBytes: number }> {
  if (identity.kind === 'single') {
    const { wav, durationMs, sizeBytes } = await kokoroClient.synthesize({
      text,
      voiceId: identity.voiceId,
    });
    return { wav, durationMs, sizeBytes };
  }
  const { wav, durationMs } = await synthesizeBlendLocal({
    text,
    spec: identity.spec,
    worldSeedHex: identity.worldSeedHex,
  });
  return { wav, durationMs, sizeBytes: wav.size };
}

export async function installPersona(
  profile: TTSProfile,
  opts: InstallOptions,
): Promise<AtomManifest> {
  if (typeof caches === 'undefined') {
    throw new Error('CACHE_STORAGE_UNAVAILABLE');
  }
  await kokoroClient.waitUntilReady();
  if (!kokoroClient.isReady()) throw new Error('KOKORO_NOT_READY');
  if (opts.identity.kind === 'blend') {
    // Blend mode needs the full 26-voice bundle for the slerp.
    await voiceBundle.ensureReady();
  }
  await ensureZeroTTSBytes();

  const fingerprint = fingerprintVoiceIdentity(
    opts.identity,
    opts.depth ?? 'classic',
  );
  const bucket = atomBucketName(profile.persona_seed, fingerprint);
  const cache = await caches.open(bucket);

  // Stage 26.6: drop orphan buckets from prior identities. Runs BEFORE the
  // synth loop so a half-completed previous install under a different pin
  // doesn't hold on to disk.
  await pruneStalePersonaBuckets(profile.persona_seed, fingerprint);

  const prior = await readManifest(profile.persona_seed);
  const priorMatches = prior?.voice_fingerprint === fingerprint;
  const manifest: AtomManifest = priorMatches && prior
    ? prior
    : {
        persona_seed: profile.persona_seed,
        profile_version: profile.version,
        installed_at: Date.now(),
        voice_id: baseVoiceIdOf(opts.identity),
        voice_identity: opts.identity,
        voice_fingerprint: fingerprint,
        atoms: {},
      };
  manifest.voice_id = baseVoiceIdOf(opts.identity);
  manifest.voice_identity = opts.identity;
  manifest.voice_fingerprint = fingerprint;

  const enumerated = enumerateAtoms(profile);
  const cap = profile.install.max_atoms ?? 100;
  if (enumerated.length > cap) {
    throw new Error(
      `[zerotts] profile exceeds max_atoms cap (${enumerated.length} > ${cap})`,
    );
  }
  enumerated.sort((a, b) => a.tier - b.tier);

  const total = enumerated.length;
  opts.onProgress?.(0, total);

  for (let i = 0; i < total; i++) {
    if (opts.signal?.aborted) {
      throw new DOMException('aborted', 'AbortError');
    }

    const atom = enumerated[i];
    const keyHex = ztHashHex(profile.persona_seed, atom.atom_text);
    const cacheKey = `/zerotts-atom/${keyHex}`;
    const req = new Request(cacheKey);

    const existing = manifest.atoms[keyHex];
    if (
      priorMatches &&
      existing?.status === 'rendered' &&
      existing.atom_text === atom.atom_text
    ) {
      const hit = await cache.match(req);
      if (hit) {
        opts.onProgress?.(i + 1, total);
        continue;
      }
    }

    const entry: AtomEntry = {
      atom_text: atom.atom_text,
      context_name: atom.context_name,
      tier: atom.tier,
      cache_key: cacheKey,
      status: 'pending',
    };

    try {
      await renderAtomIntoCache(cache, req, opts.identity, atom.atom_text, entry);
      entry.status = 'rendered';
    } catch (firstErr) {
      // eslint-disable-next-line no-console
      console.warn(
        `[zerotts] atom render failed, retrying once: "${atom.atom_text}"`,
        firstErr,
      );
      try {
        await renderAtomIntoCache(
          cache,
          req,
          opts.identity,
          atom.atom_text,
          entry,
        );
        entry.status = 'rendered';
      } catch (secondErr) {
        // eslint-disable-next-line no-console
        console.warn(
          `[zerotts] atom render failed twice, marking failed: "${atom.atom_text}"`,
          secondErr,
        );
        entry.status = 'failed';
      }
    }

    manifest.atoms[keyHex] = entry;

    if ((i + 1) % 5 === 0 || i + 1 === total) {
      await writeManifest(profile.persona_seed, manifest);
    }

    opts.onProgress?.(i + 1, total);
  }

  manifest.profile_version = profile.version;
  manifest.installed_at = Date.now();
  await writeManifest(profile.persona_seed, manifest);
  return manifest;
}

async function renderAtomIntoCache(
  cache: Cache,
  req: Request,
  identity: VoiceIdentity,
  atomText: string,
  entryOut: AtomEntry,
): Promise<void> {
  const { wav, durationMs, sizeBytes } = await renderAtom(identity, atomText);
  await cache.put(
    req,
    new Response(wav, {
      headers: {
        'Content-Type': 'audio/wav',
        'X-ZeroTTS-Duration': String(durationMs),
      },
    }),
  );
  entryOut.byte_size = sizeBytes;
  entryOut.duration_ms = durationMs;
}

// Re-export for unit tests / the runtime player cache-miss path.
export { baseVoiceIdOf as baseVoiceIdForIdentity };

/** Stage 26.6: runtime helper used by the player. */
export async function liveRenderAtom(
  identity: VoiceIdentity,
  atomText: string,
): Promise<{ wav: Blob; durationMs: number }> {
  if (identity.kind === 'blend') {
    await voiceBundle.ensureReady();
    return synthesizeBlendLocal({
      text: atomText,
      spec: identity.spec,
      worldSeedHex: identity.worldSeedHex,
    });
  }
  const { wav, durationMs } = await kokoroClient.synthesize({
    text: atomText,
    voiceId: identity.voiceId as KokoroVoiceId,
  });
  return { wav, durationMs };
}
